import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Building, Calendar, StickyNote, Plus, Trash2, CheckCircle } from 'lucide-react';
import { COMPANY } from '../../../config/company';

// Construction flow steps configuration
const CONSTRUCTION_STEPS = [
  { key: 'contract_signed', label: 'Contract Signed', label_es: 'Contrato Firmado', phase: 'pre' },
  { key: 'permit_applied', label: 'Permit Applied For', label_es: 'Permiso Solicitado', phase: 'pre' },
  { key: 'permit_granted', label: 'Permit Granted', label_es: 'Permiso Otorgado', phase: 'pre' },
  { key: 'hoa_filed', label: 'HOA Application Filed', label_es: 'Solicitud HOA Presentada', phase: 'pre' },
  { key: 'hoa_granted', label: 'HOA Application Granted', label_es: 'Solicitud HOA Aprobada', phase: 'pre' },
  { key: 'materials_ordered', label: 'Materials Ordered from Manufacturer', label_es: 'Materiales Ordenados del Fabricante', phase: 'materials' },
  { key: 'delivery_date_set', label: 'Materials Delivery Date Set', label_es: 'Fecha de Entrega Establecida', phase: 'materials' },
  { key: 'materials_delivered', label: 'Materials Delivered', label_es: 'Materiales Entregados', phase: 'materials' },
  { key: 'materials_confirmed', label: 'Materials Confirmed', label_es: 'Materiales Confirmados', phase: 'materials' },
  { key: 'change_orders_submitted', label: 'Change Orders Submitted', label_es: 'Órdenes de Cambio Enviadas', phase: 'materials' },
  { key: 'subcontractors_hired', label: 'Subcontractors Hired', label_es: 'Subcontratistas Contratados', phase: 'construction' },
  { key: 'demolition_begins', label: 'Demolition Begins', label_es: 'Demolición Comienza', phase: 'construction' },
  { key: 'rotted_wood_replaced', label: 'Rotted Wood Replaced', label_es: 'Madera Podrida Reemplazada', phase: 'construction' },
  { key: 'demolition_completed', label: 'Demolition Completed', label_es: 'Demolición Completada', phase: 'construction' },
  { key: 'underlayment_installed', label: 'Underlayment Installed', label_es: 'Membrana Instalada', phase: 'construction' },
  { key: 'water_shield_installed', label: 'Water Shield Installed', label_es: 'Protección de Agua Instalada', phase: 'construction' },
  { key: 'first_inspection_called', label: 'First Inspection Called In', label_es: 'Primera Inspección Solicitada', phase: 'inspection' },
  { key: 'first_inspection_passed', label: 'First Inspection Passed', label_es: 'Primera Inspección Aprobada', phase: 'inspection' },
  { key: 'second_payment_paid', label: 'Second Payment Invoiced and Paid', label_es: 'Segundo Pago Facturado y Pagado', phase: 'payment' },
  { key: 'first_quarter_installed', label: 'First Quarter Tile Installed', label_es: 'Primer Cuarto de Teja Instalado', phase: 'construction' },
  { key: 'progress_inspection', label: 'Progress Inspection Completed', label_es: 'Inspección de Progreso Completada', phase: 'inspection' },
  { key: 'second_third_installed', label: '2nd Third of Roof Installed', label_es: 'Segundo Tercio del Techo Instalado', phase: 'construction' },
  { key: 'wind_lift_test', label: 'Wind Lift Test Completed', label_es: 'Prueba de Elevación por Viento Completada', phase: 'inspection' },
  { key: 'final_inspection', label: 'Final Inspection Completed', label_es: 'Inspección Final Completada', phase: 'inspection' },
  { key: 'final_payment_invoiced', label: 'Final Payment Invoiced', label_es: 'Pago Final Facturado', phase: 'payment' },
  { key: 'final_payment_made', label: 'Final Payment Made', label_es: 'Pago Final Realizado', phase: 'payment' },
  { key: 'project_closed', label: 'Project Closed', label_es: 'Proyecto Cerrado', phase: 'closed' },
];

const PHASE_COLORS = {
  pre: 'border-l-blue-500',
  materials: 'border-l-purple-500',
  construction: 'border-l-orange-500',
  inspection: 'border-l-green-500',
  payment: 'border-l-amber-500',
  closed: 'border-l-emerald-600'
};

const LeadProjectTab = ({
  language,
  lead,
  handleInputChange,
  newProjectNote,
  setNewProjectNote,
  addProjectNote,
  deleteProjectNote
}) => {
  return (
    <Card data-testid="lead-project-tab">
      <CardHeader style={{ backgroundColor: COMPANY.primaryColor }} className="text-white rounded-t-lg">
        <CardTitle className="flex items-center">
          <Building className="mr-2 h-5 w-5" />
          {language === 'es' ? 'Flujo de Construcción' : 'Construction Flow Chart'}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6 space-y-4">
        {/* Basic Project Info */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <Label>{language === 'es' ? 'Número de Trabajo' : 'Job Number'}</Label>
            <Input 
              value={lead.proposal_id || lead.estimate_number || lead.job_number || ''} 
              readOnly
              className="bg-gray-100 cursor-not-allowed"
              title={language === 'es' ? 'Auto-generado desde número de estimado' : 'Auto-generated from estimate number'}
            />
          </div>
          <div>
            <Label className="flex items-center">
              <Calendar className="mr-1 h-4 w-4" />
              {language === 'es' ? 'Fecha del Presupuesto' : 'Estimate Date'}
            </Label>
            <Input 
              type="date" 
              value={lead.estimate_date || ''} 
              onChange={(e) => handleInputChange('estimate_date', e.target.value)} 
            />
          </div>
        </div>

        {/* Construction Flow Chart */}
        <div className="border-t pt-4">
          {/* Project Notes at Top Center */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <Label className="text-lg font-semibold flex items-center mb-3">
              <StickyNote className="mr-2 h-5 w-5 text-yellow-600" />
              {language === 'es' ? 'Notas del Proyecto' : 'Project Notes'}
            </Label>
            
            {/* Add New Note */}
            <div className="flex gap-2 mb-3">
              <Textarea 
                value={newProjectNote}
                onChange={(e) => setNewProjectNote(e.target.value)}
                placeholder={language === 'es' ? 'Agregar una nota...' : 'Add a note...'}
                className="flex-1 bg-white"
                rows={2}
                data-testid="new-project-note-textarea"
              />
              <Button 
                onClick={addProjectNote}
                disabled={!newProjectNote.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 self-end"
                data-testid="add-project-note-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Notes List */}
            {(lead.project_notes || []).length > 0 && (
              <div className="space-y-2 max-h-[150px] overflow-y-auto">
                {(lead.project_notes || []).map((note) => (
                  <div key={note.id} className="flex items-start gap-2 p-2 bg-white rounded border text-sm">
                    <span className="flex-1">{note.text}</span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{note.date}</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-6 w-6 p-0 text-red-500"
                      onClick={() => deleteProjectNote(note.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <h3 className="text-lg font-semibold mb-4">
            {language === 'es' ? 'Progreso del Proyecto' : 'Project Progress'}
          </h3>
          
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {CONSTRUCTION_STEPS.map((step, index) => {
              const isCompleted = lead.construction_progress?.[step.key] || false;
              const completedDate = lead.construction_dates?.[step.key] || '';
              const stepNote = lead.construction_notes?.[step.key] || '';
              
              return (
                <div 
                  key={step.key} 
                  className={`p-3 rounded-lg border-l-4 ${PHASE_COLORS[step.phase]} ${isCompleted ? 'bg-green-50' : 'bg-gray-50'}`}
                  data-testid={`construction-step-${step.key}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-gray-400 text-sm font-mono w-6">{index + 1}.</span>
                    <input
                      type="checkbox"
                      checked={isCompleted}
                      onChange={(e) => {
                        const newProgress = { ...(lead.construction_progress || {}), [step.key]: e.target.checked };
                        handleInputChange('construction_progress', newProgress);
                        // Auto-set date when checked
                        if (e.target.checked && !completedDate) {
                          const newDates = { ...(lead.construction_dates || {}), [step.key]: new Date().toISOString().split('T')[0] };
                          handleInputChange('construction_dates', newDates);
                        }
                      }}
                      className="h-5 w-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className={`flex-1 ${isCompleted ? 'line-through text-gray-500' : 'text-gray-800'}`}>
                      {language === 'es' ? step.label_es : step.label}
                    </span>
                    <input
                      type="date"
                      value={completedDate}
                      onChange={(e) => {
                        const newDates = { ...(lead.construction_dates || {}), [step.key]: e.target.value };
                        handleInputChange('construction_dates', newDates);
                      }}
                      className="text-xs border rounded px-2 py-1 w-32"
                      placeholder="Date"
                    />
                    {isCompleted && <CheckCircle className="h-5 w-5 text-green-600" />}
                  </div>
                  {/* Note field for each step */}
                  <div className="ml-8">
                    <Input
                      value={stepNote}
                      onChange={(e) => {
                        const newNotes = { ...(lead.construction_notes || {}), [step.key]: e.target.value };
                        handleInputChange('construction_notes', newNotes);
                      }}
                      placeholder={language === 'es' ? 'Agregar nota...' : 'Add note...'}
                      className="text-sm h-8 bg-white"
                    />
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Phase Legend */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-blue-500 rounded"></span> Pre-Construction
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-purple-500 rounded"></span> Materials
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-orange-500 rounded"></span> Construction
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-green-500 rounded"></span> Inspection
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-amber-500 rounded"></span> Payment
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 bg-emerald-600 rounded"></span> Closed
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadProjectTab;
